/**
 * Dependencies required:
 * npm install react-google-recaptcha @types/react-google-recaptcha
 * 
 * Configuration needed:
 * 1. Replace 'YOUR_RECAPTCHA_SITE_KEY' with your actual Google reCAPTCHA v2 site key
 * 2. Replace 'YOUR_ENDPOINT_HERE' with your actual API endpoint
 */

import React, { useState, FormEvent, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import Button from '../components/Button';
import CookieBanner from '../components/CookieBanner';
import logo from '../assets/zwyk-logo-white3.svg';
import '../styles/AdrenaliniumForm.css';
import AOS from "aos";
import "aos/dist/aos.css";
import adrenaliniumBadge from '../assets/adrenalinium_odznaka_main.svg';

const AdrenaliniumForm: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [wantsAdditionalQuestions, setWantsAdditionalQuestions] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [additionalAnswers, setAdditionalAnswers] = useState({
    answer1: '',
    answer2: '',
    answer3: '',
  });
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const sitekey = '6Lcf_OgrAAAAAOAPjheV_M7jrMQEFwTOXLkOsE5b'

  useEffect(() => {
    AOS.init({
      once: true,
      duration: 600,
      easing: "ease-out-sine",
    });
}, []);

  const handleSubmit = async (e: FormEvent) => {
    // If user wants additional questions and we haven't shown them yet
    // if (wantsAdditionalQuestions && !showAdditionalQuestions) {
    //   setShowAdditionalQuestions(true);
    //   return;
    // }

    e.preventDefault();
    if (!captchaToken) {
      setShowCaptcha(true);
      return;
    }
    
    if (captchaToken) {
      setIsSubmitting(true);
      setSubmitStatus('idle');

      try {
        // Determine endpoint based on current hostname
        const isDev = window.location.hostname === 'dev.zwyk.pl';
        const endpoint = isDev 
          ? 'https://dev.api.zwyk.pl/event/register'
          : 'https://api.zwyk.pl/event/register';
        
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            email, 
            details: { ...(wantsAdditionalQuestions && 
              { additionalAnswers, dataProcessingConsent: consentGiven, marketingConsent }) },
            token: captchaToken
          })
        });
        console.log('response', response);
        setSubmitStatus('success');
        navigate('/adrenalinium-2025/success');
      } catch (error) {
        setSubmitStatus('error');
        console.error('Submission error:', error);
        recaptchaRef.current?.reset();
        setCaptchaToken(null);
      } finally {
        setIsSubmitting(false);
      }
    }

  };

  return (
    <div className="adrenalinium-page">
      <div className="adrenalinium-container">
        <div className="adrenalinium-header">
          <img width={100} height={'auto'} src={logo} alt="Zwyk Logo" />
        </div>
        
        <div className="adrenalinium-content">
          <h1 className="adrenalinium-title">Adrenalinium <span className="zwyk-color">2025</span></h1>
          <img width={150} height={'auto'} src={adrenaliniumBadge} alt="Adrenalinium Badge" className="adrenalinium-badge" />
          <form onSubmit={handleSubmit} className="adrenalinium-form">
              <>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="twoj.email@example.com"
                    required
                    disabled={isSubmitting}
                    className="email-input"
                  />
                </div>

                <div className="form-checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={wantsAdditionalQuestions}
                      onChange={(e) => setWantsAdditionalQuestions(e.target.checked)}
                      disabled={isSubmitting}
                      className="checkbox-input"
                    />
                    <span>Chcę odpowiedzieć na dodatkowe pytania</span>
                  </label>
                </div>
              </>
              {wantsAdditionalQuestions && (
              <div className="additional-questions" data-aos="fade-up" data-aos-delay={50}>
                <div className="form-group">
                  <label className="question-label"><b>Czy nasza inicjatywa wydaje ci się ciekawa i warta uwagi?</b></label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="answer1"
                        value="Tak"
                        checked={additionalAnswers.answer1 === "Tak"}
                        onChange={(e) => setAdditionalAnswers({...additionalAnswers, answer1: e.target.value})}
                        disabled={isSubmitting}
                      />
                      <span>Tak</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="answer1"
                        value="Nie"
                        checked={additionalAnswers.answer1 === "Nie"}
                        onChange={(e) => setAdditionalAnswers({...additionalAnswers, answer1: e.target.value})}
                        disabled={isSubmitting}
                      />
                      <span>Nie</span>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label className="question-label"><b>Czy chciał(a)byś mieć dostęp do przedpremierowej (testowej) wersji aplikacji ZWYK?</b></label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="answer2"
                        value="Tak"
                        checked={additionalAnswers.answer2 === "Tak"}
                        onChange={(e) => setAdditionalAnswers({...additionalAnswers, answer2: e.target.value})}
                        disabled={isSubmitting}
                      />
                      <span>Tak</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="answer2"
                        value="Jeszcze się zastanowię!"
                        checked={additionalAnswers.answer2 === "Jeszcze się zastanowię!"}
                        onChange={(e) => setAdditionalAnswers({...additionalAnswers, answer2: e.target.value})}
                        disabled={isSubmitting}
                      />
                      <span>Jeszcze się zastanowię!</span>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label className="question-label"><b>Czy prowadzisz biznes związany z turystyką górską?</b></label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="answer3"
                        value="Tak"
                        checked={additionalAnswers.answer3 === "Tak"}
                        onChange={(e) => setAdditionalAnswers({...additionalAnswers, answer3: e.target.value})}
                        disabled={isSubmitting}
                      />
                      <span>Tak</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="answer3"
                        value="Nie"
                        checked={additionalAnswers.answer3 === "Nie"}
                        onChange={(e) => setAdditionalAnswers({...additionalAnswers, answer3: e.target.value})}
                        disabled={isSubmitting}
                      />
                      <span>Nie</span>
                    </label>
                  </div>
                </div>
              </div>
              )}

              <div className="form-checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={consentGiven}
                      onChange={(e) => setConsentGiven(e.target.checked)}
                      disabled={isSubmitting}
                      className="checkbox-input"
                      required
                    />
                    <span>Wyrażam zgodę na przetwarzanie mojego adresu e-mail i odpowiedzi ankietowych przez ZWYK 
                      w celu realizacji akcji „Adrenalinium x ZWYK" oraz otrzymania odznaczenia 
                      i dostępu do wersji testowej aplikacji ZWYK. <i>(wymagane)*</i></span>
                  </label>
                </div>

                <div className="form-checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={marketingConsent}
                      onChange={(e) => setMarketingConsent(e.target.checked)}
                      disabled={isSubmitting}
                      className="checkbox-input"
                    />
                    <span>Wyrażam zgodę na otrzymywanie informacji handlowych 
                      i marketingowych drogą e-mailową dotyczących aplikacji ZWYK. <i>(opcjonalne)</i></span>
                  </label>
                </div>

            <div className="recaptcha-container">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={sitekey}
                    onChange={(token: string | null) => setCaptchaToken(token)}
                    onExpired={() => setCaptchaToken(null)}
                    theme="dark"
                  />
            </div>

            <div className="form-button">
              <Button type="submit" disabled={isSubmitting || !captchaToken || email === '' || !consentGiven}>
                Zapisz się
              </Button>
            </div>

            {submitStatus === 'error' && (
              <div className="status-message error">
                {!captchaToken ? 'Proszę potwierdzić, że nie jesteś robotem.' : 'Wystąpił błąd. Spróbuj ponownie.'}
              </div>
            )}
          </form>
        </div>
      </div>
      <CookieBanner />
    </div>
  );
};

export default AdrenaliniumForm;

